import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { BehaviorSubject, Observable, catchError, map, of } from 'rxjs';
import { CookieBannerService } from './cookie-banner.service';
import { ObservableDataLoaderService } from './observable-data-loader.service';
import { StorageService } from './storage.service';

export interface Rating {
  name: string;
  min: number;
  max: number;
  average: number;
  total: number;
}

@Injectable({
  providedIn: 'root',
})
export class RatingsService {
  private http = inject(HttpClient);
  private loader = inject(ObservableDataLoaderService);
  private storage = inject(StorageService);
  private cookieBannerService = inject(CookieBannerService);

  public rateToast$ = new BehaviorSubject<{ ratingName: string; title: string; description: string }>(null);

  public getCustomRatings(): Observable<Rating[]> {
    return this.loader.get('custom-ratings', () => {
      return this.http.get<Rating[]>(`https://picflow.com/internal-api/custom-ratings`).pipe(
        map(ratings => {
          if (!Array.isArray(ratings)) {
            console.warn('invalid_custom_ratings_response', typeof ratings);
            console.warn(ratings);
            return [];
          }
          return ratings;
        }),
        catchError(() => of([]))
      );
    });
  }

  public calcRating(ratings: Rating[], ratingNameQuery: string): Rating {
    if (!ratings) return;
    return this.combineRatings(this.findRelevantRatings(ratings, ratingNameQuery));
  }

  private findRatingsByQuery(allRatings: Rating[], query: string): Rating[] {
    const ratings = allRatings.filter((r: Rating) => r.name === query);

    // Find ratings that contain a * in the query
    // For example: 'convert_*'
    if (query.endsWith('*')) {
      const queryPrefix = query.slice(0, -1);
      const ratingsWithPrefix = allRatings.filter((r: Rating) => r.name.startsWith(queryPrefix));
      if (!ratings.find((r: Rating) => r.name === queryPrefix)) {
        ratings.push(...ratingsWithPrefix);
      }
    }

    return ratings;
  }

  private findRelevantRatings(ratings: Rating[], ratingNameQuery: string): Rating[] {
    const ratingNameQueries: string[] = ratingNameQuery.split(',').map((r: string) => r.trim());
    const relevantRatings: Rating[] = [];
    for (const query of ratingNameQueries) {
      relevantRatings.push(...this.findRatingsByQuery(ratings, query));
    }
    return relevantRatings;
  }

  private combineRatings(ratings: Rating[]): Rating {
    if (!ratings.length) return;
    return ratings.reduce(
      (acc: Rating, r: Rating) => {
        acc.average = Math.round(((acc.average * acc.total + r.average * r.total) / (acc.total + r.total)) * 100) / 100;
        acc.max = Math.max(acc.max, r.max);
        acc.min = Math.min(acc.min, r.min);
        acc.total += r.total;
        return acc;
      },
      <Rating>{ name: 'AGGREGATE', average: 0, max: 0, min: 0, total: 0 }
    );
  }

  public showRatingOption(ratingName: string, title: string, description: string) {
    if (this.storage.get(`rating_${ratingName}`)) return;
    this.rateToast$.next({ ratingName, title, description });
    this.cookieBannerService.hide();
  }

  public rate(ratingName: string, rating: number, message?: string) {
    this.storage.set(`rating_${ratingName}`, true);
    this.loader.update<Rating[]>('custom-ratings', ratings => {
      ratings.forEach(_rating => {
        if (_rating.name === ratingName) {
          _rating.total += 1;
          _rating.average = (_rating.average * _rating.total + rating) / (_rating.total + 1);
        }
      });

      return ratings;
    });

    this.http.post('/internal-api/custom-ratings', { ratingName, rating, message }).subscribe();
  }

  public hideRatingOption(ratingName: string) {
    this.storage.set(`rating_${ratingName}`, true);
    this.rateToast$.next(null);
  }
}
